import { useCallback, useEffect, useRef, useState } from 'react';
import { getDeviceUuid, getMedication, uploadMedicationImage, deleteMedicationImage } from '../api';
import { getScreenCacheContextRevision } from '../utils/localCache';
import { syncWidget } from '../utils/widgetSync';

// One mutation per editor, with no optimistic replacement of a persisted photo.
export default function useMedicationPhotoEditing({ id, imageUrl, compress, onSaved, onError }) {
    const [saving, setSaving] = useState(false);
    const busy = useRef(false);
    const lifetime = useRef(0);
    const currentId = useRef(id);
    currentId.current = id;
    useEffect(() => () => { lifetime.current++; }, []);

    const mutate = useCallback(async (file, remove) => {
        if (!id || busy.current || (!remove && !file)) return;
        busy.current = true;
        setSaving(true);
        const uuid = getDeviceUuid();
        const generation = lifetime.current;
        const context = getScreenCacheContextRevision();
        const isCurrent = () => generation === lifetime.current && currentId.current === id
            && getDeviceUuid() === uuid && getScreenCacheContextRevision() === context;
        try {
            if (remove) {
                await deleteMedicationImage(uuid, id, imageUrl || '');
                if (isCurrent()) onSaved('');
            } else {
                const compressed = await compress(file);
                if (!isCurrent()) return;
                const result = await uploadMedicationImage(uuid, id, compressed, imageUrl || '');
                if (!result?.imageUrl) throw new Error('missing saved image URL');
                if (isCurrent()) onSaved(result.imageUrl);
            }
            // Widget refresh failure must not turn a committed photo into an upload failure.
            if (isCurrent()) Promise.resolve().then(() => syncWidget(uuid)).catch(() => {});
        } catch (error) {
            // A lost response or conflict can still mean the server has a NEW image. Reconcile it.
            if (isCurrent()) {
                try {
                    const latest = await getMedication(uuid, id);
                    if (isCurrent()) onSaved(latest.imageUrl || '');
                } catch { /* Keep the last confirmed photo, never an unsaved local preview. */ }
                if (isCurrent()) onError(error, remove);
            }
        } finally {
            busy.current = false;
            if (generation === lifetime.current) setSaving(false);
        }
    }, [id, imageUrl, compress, onSaved, onError]);
    return { saving, busy, save: file => mutate(file, false), remove: () => mutate(null, true) };
}
